(ns manatee.components.item
  (:require
   [clojure.set :refer [rename-keys]]
   [clojure.string :as str]
   [garden.color :as colour]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.annotation.autosaving :as autosaving]
   [manatee.annotation.handlers :as handlers]
   [manatee.annotation.subs :as annotation]
   [manatee.components.answer :as answer-components]
   [manatee.components.hybrid-part-answer :as hybrid-part-answer-components]
   [manatee.components.part :as part-components]
   [manatee.domain.task :as task-config]
   [manatee.edit.subs :as edit-subs]
   [manatee.models.differences :as differences]
   [manatee.models.item :as item-model]
   [manatee.models.segment :as segment-model]
   [manatee.spec.core :as spec]
   [manatee.ui.styles :as manatee-styles]
   [re-frame.core :as rf]
   [rothko.editor.document :as document]
   [rothko.editor.manatee.markers :as markers]))

(defn field->prop
  [item {:keys [field-name] :as field}]
  (-> field
      (update :options (fn [opt] (map #(rename-keys % {:label :representation}) opt)))
      (assoc :init-value (get-in item [:edit field-name]))
      (rename-keys {:label :placeholder
                    :field-name :score-name})
      (update :score-name keyword)
      (assoc :spec ::spec/answer-score)
      (assoc :messages {::spec/answer-score (str "You must select a " (when field (name field-name)))})))

(defn annotate-part-item
  [item task-config scoring-task? children]
  (let [edits @(rf/subscribe [::edit-subs/edits])
        answers @(rf/subscribe [::annotation/items-by-type :answer])
        unannotated-answers @(rf/subscribe [::annotation/unannotated-items-by-type :answer])
        incomplete-feedback? @(rf/subscribe [::annotation/incomplete-feedback?])
        ;; TODO multiple part score fields may be supported in future
        part-score-props (field->prop item (get-in task-config [:data :part :fields 0]))
        item-props {:item item
                    :edits edits
                    :answers answers
                    :task-config task-config}
        props (merge item-props
                ;; this assumes we only have scoring and transcribing task types
                     (when scoring-task?
                       (assoc part-score-props :unannotated-answers unannotated-answers))
                     (when (:feedback? item) {:incomplete-feedback? incomplete-feedback?}))]
    (-> [part-components/part-panel props]
        (conj (when (and scoring-task?
                         (-> task-config :data :part :fields seq))
                [part-components/score-selector props]))
        (into children))))

(defn annotate-answer-scoring-task
  [{:keys [item task-config scoring-disabled?]}]
  (let [part @(rf/subscribe [::annotation/primary-item])
        auto-scores (:auto-scores part)]
    [answer-components/score
     {:task-config task-config
      :scorable item
      :disabled? scoring-disabled?
      :auto-scores auto-scores}]))

(defn qa-editor-unsubmitted?
  [{:keys [id qa-edit-id edit]
    {:keys [submitted-comparate-edit]} :edit}
   task-config]
  (when (and qa-edit-id
             (task-config/form-based? task-config :answer))
    (let [intermediate-edit @(rf/subscribe [::annotation/intermediate-edit id qa-edit-id])
          {:keys [field-name]} (task-config/non-scoring-field task-config)]
      (not=
       (differences/remove-flags
        (get (or submitted-comparate-edit edit) field-name))
       (differences/remove-flags
        (get intermediate-edit field-name))))))

(defn editor-info-text
  [{:keys [item autosaving-disabled? segment-annotation? task-config]}]
  (let [assessment-name @(rf/subscribe [::annotation/assessment-name])
        same-local-and-submitted-document? @(rf/subscribe [::annotation/same-local-and-submitted-document?])
        autosaving? @(rf/subscribe [::annotation/autosaving?])
        word-count (:word-count item)
        show-word-count? (not (task-config/modifier task-config :hide-word-count?))]
    [:div.editor-info-text
     (when (qa-editor-unsubmitted? item task-config)
       [:p.editor-info-left "You have unsubmitted changes."])
     [:p.editor-info-left (when assessment-name (str "Assessment: " assessment-name))]
     [:p.editor-info-left (when-not (or same-local-and-submitted-document?
                                        autosaving-disabled?
                                        segment-annotation?)
                            "Displayed and submitted transcripts are not the same.")]
     [:p.editor-info-right (when autosaving? "Autosaving...")]

     (when show-word-count?
       [:p.editor-info-right (when word-count (str "Word count: " word-count))])]))

(def field-type->editor-form
  {:transcript-annotation answer-components/transcript-editor-form
   :segment-transcript-annotation answer-components/transcript-editor-form
   :error-annotation answer-components/error-editor-form
   :script-annotation answer-components/script-editor-form
   :mwe-annotation answer-components/mwe-editor-form})

(def no-segment-prompt [:div "To begin annotating, draw or select a segment on the sound wave. Right-click a segment, to play it."])

(defn make-editor-event-opts
  [props]
  (select-keys props [:item :task-config :autosaving-disabled? :spellcheck-disabled? :segment-annotation?]))

(defn segment-editor-document
  [item-id]
  (or @(rf/subscribe [::annotation/segment-document item-id])
      (document/scripts->document [] [])))

(defn editor-document
  [{:keys [item autosaving-disabled?]} field-name]
  (let [script-on-local-storage (when-not autosaving-disabled?
                                  @(rf/subscribe [::autosaving/autosaved-script (:id item)]))]
    (document/scripts->document
     (or script-on-local-storage
         (item-model/appropriate-script item field-name))
     (field-name item))))

;; The following is defined in the format of the task-config.
;; The label, however, is not actually used, as the toolbar constructors
;; (in src/client/manatee/components/editors/toolbar.cljs) have their own version.
;; The :value occurs in both places, and must match.
(def default-markers [{:value "disfluency"    :label "Disfluency"    :rgb "255 140 0"}
                      {:value "pronunciation" :label "Pronunciation" :rgb "255 13 111"}
                      {:value "partial"       :label "Partial"       :rgb "141 0 255"}
                      {:value "backchannel"   :label "Backchannel"   :rgb "168 194 86"}
                      {:value "spelling"      :label "Spelling"      :rgb "255 140 0"}
                      {:value "discontinuity" :label "Discontinuity" :rgb "255 140 0"}])

(defn ->rothko-markers
  [markers]
  (->> markers
       (map (fn [{:keys [value rgb]}] [(keyword value) (colour/rgb (mapv #(js/parseInt %) (str/split rgb #" ")))]))
       (into {})))

(defn annotate-answer-editor-task
  [{:keys [task-config segment-annotation?] {:keys [id]} :item :as props}]
  (let [{:keys [field-name field-type]} (task-config/non-scoring-field task-config)
        document (if segment-annotation? (segment-editor-document id) (editor-document props field-name))
        editor-event-opts (make-editor-event-opts props)
        answer-score-id (task-config/answer-score-id task-config)
        custom-markers (task-config/custom-markers task-config)
        custom-styling (task-config/custom-styling task-config)
        immutable-text? (task-config/modifier task-config :immutable-text?)
        show-ai-hybrid? (task-config/modifier task-config :show-ai-hybrid?)
        editor-props (assoc props
                            :on-mount #(rf/dispatch [::handlers/on-editor-mount editor-event-opts %])
                            :on-transform #(rf/dispatch [::handlers/on-editor-transform editor-event-opts %])
                            :on-change-view #(rf/dispatch [::handlers/on-editor-change-view editor-event-opts %])
                            :initial-view (when segment-annotation? @(rf/subscribe [::annotation/editor-view id]))
                            :document (dissoc document ::segment-model/update-key)
                            :custom-markers custom-markers
                            :immutable-text? immutable-text?
                            :show-ai-hybrid? show-ai-hybrid?
                            :answer-score-id answer-score-id
                            :answer-score-options (task-config/score-options task-config answer-score-id)
                            :field-name field-name
                            :editor-event-opts editor-event-opts)
        editor-form (field-type->editor-form field-type)
        segment-id (when segment-annotation? @(rf/subscribe [::annotation/current-segment-id id]))]
    (markers/install-markers (->rothko-markers (or custom-markers default-markers)))
    (manatee-styles/dynamic-style :task-config custom-styling)
    (if (and segment-annotation? (not segment-id))
      no-segment-prompt
      ^{:key (or (::segment-model/update-key document segment-id)
                 id)}
      [:div
       [editor-form editor-props]
       [editor-info-text props]])))

(defn annotate-answer-item
  [item task-config scoring-task? props children]
  (let [answer-props (answer-components/make-answer-props item task-config scoring-task? props)]
    (-> [answer-components/answer-panel answer-props
         (if scoring-task?
           (annotate-answer-scoring-task answer-props)
           (annotate-answer-editor-task answer-props))]
        (into children))))

(defn- annotate-hybrid-part-answer-item
  [item task-config child-components]
  (let [answers @(rf/subscribe [::annotation/items-by-type :answer])
        incomplete-feedback? @(rf/subscribe [::annotation/incomplete-feedback?])
        part-score-field-props (field->prop item
                                            (get-in task-config [:data :part :fields 0]))
        part-item-props {:item item
                         :answers answers
                         :task-config task-config}
        props (merge part-item-props
                     part-score-field-props
                     (when (:feedback? item)
                       {:incomplete-feedback? incomplete-feedback?}))]
    (-> (hybrid-part-answer-components/part+answers-panel props)
        (conj (part-components/score-selector props))
        (into child-components))))

(defn annotate-item
  [item props & children]
  (let [task-config @(rf/subscribe [::task-config-subs/task-config (:task-config-id item)])
        scoring-task? (task-config/scoring? task-config)]
    (when task-config
      (if (= :part
             (:item-type item))
        (if (task-config/modifier task-config :hybrid-part-answers?)
          (annotate-hybrid-part-answer-item item task-config children)
          (annotate-part-item item task-config scoring-task? children))
        (annotate-answer-item item task-config scoring-task? props children)))))
